/**
 * Pure geometry for <magnify-dialog> (assets/magnify-dialog.js).
 *
 * No DOM access here, so every rule the lightbox applies to a gesture can be
 * replayed and tested on its own. Offsets are the `translate()` of an image
 * whose transform-origin is its top-left corner: a point p of the untransformed
 * box is painted at boxStart + offset + p * scale.
 */
window.MagnifyGeometry = Object.freeze({
  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  },

  distance(first, second) {
    return Math.hypot(second.x - first.x, second.y - first.y);
  },

  midpoint(first, second) {
    return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  },

  /**
   * Keeps one axis of a scaled image on screen. `boxStart` is where the
   * untransformed box starts, measured from the viewport's start. An image
   * smaller than the viewport is centred; a larger one may never show a gap
   * at either edge.
   */
  clampAxis(offset, boxStart, boxSize, scale, viewportSize) {
    const scaledSize = boxSize * scale;
    if (scaledSize <= viewportSize) return (viewportSize - scaledSize) / 2 - boxStart;
    return this.clamp(offset, viewportSize - scaledSize - boxStart, -boxStart);
  },

  /**
   * The offset that keeps `point` (same coordinates as `boxStart`) under the
   * finger while the scale moves from `scale` to `nextScale`.
   */
  offsetAround(offset, scale, nextScale, point, boxStart) {
    const local = (point - boxStart - offset) / scale;
    return point - boxStart - local * nextScale;
  },

  /** Two taps close enough in time and space to be one double tap. */
  isDoubleTap(previous, current, maxDelay, maxDistance) {
    if (!previous || !current) return false;
    if (current.time - previous.time > maxDelay) return false;
    return this.distance(previous, current) <= maxDistance;
  },

  /** Index of the slide a horizontal scroller is resting on. */
  slideIndex(scrollLeft, slideWidth, count) {
    if (!slideWidth || !count) return 0;
    return this.clamp(Math.round(scrollLeft / slideWidth), 0, count - 1);
  },
});
